import { useEffect, useState } from 'react';
import axios from 'axios';
import env from 'react-dotenv';
import { useAuthHeader, useAuthUser } from 'react-auth-kit';
import { Ban, KeyRound, MonitorSmartphone, PauseCircle, PlayCircle, Plus, RefreshCw, Tv, Wifi } from 'lucide-react';
import User from '../../Models/User';
import datas from '../../Assets/data.json';
import { Crypter } from '../../Functions/Security/Crypter';
import { Button, DataTable, Modal, Select, Input, Spin, Badge, useAlert, PageHeader, TableStatusBar } from '../ui';
import { DataTableColumn } from '../ui/DataTable';
import GestionEcrans from './GestionEcrans';

/**
 * Supervision des bornes self-service.
 * - LIST : bornes du tenant (Statut, filiale, dernière activité)
 * - SUSPEND / REACTIVATE / REVOKE via ManageBornes (BorneId)
 * - Validation des codes de pairing affichés sur les bornes (ValidatePairing)
 */
const GestionBornes = () => {
    const authHeader = useAuthHeader()
    const auth = useAuthUser()
    const user: User = JSON.parse(JSON.stringify(auth() || {}))
    const alert = useAlert()

    const [rows, setRows] = useState<any[]>([])
    const [loading, setLoading] = useState(false)
    const [filiales, setFiliales] = useState<any[]>([])
    const [filtreFiliale, setFiltreFiliale] = useState('')
    const [tab, setTab] = useState<'bornes' | 'ecrans'>('bornes')

    // Modale pairing
    const [openPairing, setOpenPairing] = useState(false)
    const [pairCode, setPairCode] = useState('')
    const [pairFiliale, setPairFiliale] = useState('')
    const [pairLibelle, setPairLibelle] = useState('')
    const [pairing, setPairing] = useState(false)

    // Confirmation d'action cycle de vie
    const [confirm, setConfirm] = useState<{ option: 'REVOKE' | 'SUSPEND' | 'REACTIVATE'; borne: any } | null>(null)

    useEffect(() => {
        LoadBornes()
        LoadFiliales()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    function headers(extra?: any) {
        return { 'Content-Type': 'application/json', 'Authorization': authHeader(), ...extra }
    }

    function LoadBornes() {
        setLoading(true)
        const payload: any = { Option: 'LIST', CodeAbonne: user.CodeAbonne }
        if (filtreFiliale) payload.CodeFiliale = filtreFiliale
        axios.request({
            method: 'post', maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.Borne.ManageBornes,
            headers: headers(),
            data: Crypter.encrypt(JSON.stringify(payload)),
        })
            .then((res) => {
                setLoading(false)
                const v = JSON.parse(Crypter.decrypt(res.data)).Value
                if (v.StatutCode == 200 || v.StatutCode === 'OK') {
                    setRows((Array.isArray(v.Result) ? v.Result : []).map((b: any, i: number) => ({ id: b.BorneId ?? i, ...b })))
                } else {
                    setRows([])
                    alert.warning(v.Message)
                }
            })
            .catch((e) => { setLoading(false); alert.error(e.message) })
    }

    function LoadFiliales() {
        axios.request({
            method: 'get', maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.GetAllFiliales + Crypter.encrypt(user.CodeAbonne),
            headers: { 'Accept': 'text/plain', 'Authorization': authHeader() },
        })
            .then((res) => {
                const v = JSON.parse(Crypter.decrypt(res.data)).Value
                if (v.StatutCode == 200) setFiliales(Array.isArray(v.Result) ? v.Result : [])
            })
            .catch(() => { })
    }

    /** REVOKE / SUSPEND / REACTIVATE. */
    function manageBorne(option: 'REVOKE' | 'SUSPEND' | 'REACTIVATE', borne: any) {
        setConfirm(null)
        setLoading(true)
        axios.request({
            method: 'post', maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.Borne.ManageBornes,
            headers: headers(),
            data: Crypter.encrypt(JSON.stringify({ Option: option, BorneId: borne.BorneId })),
        })
            .then((res) => {
                setLoading(false)
                const v = JSON.parse(Crypter.decrypt(res.data)).Value
                if (v.StatutCode == 200 || v.StatutCode === 'OK') {
                    alert.success(v.Message)
                    LoadBornes()
                } else alert.warning(v.Message)
            })
            .catch((e) => { setLoading(false); alert.error(e.message) })
    }

    /** Validation du code de pairing affiché sur la borne. */
    function validatePairing() {
        if (!pairCode.trim() || !pairFiliale) { alert.warning('Code de pairing et filiale obligatoires'); return }
        setPairing(true)
        axios.request({
            method: 'post', maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.Borne.ValidatePairing,
            headers: headers(),
            data: Crypter.encrypt(JSON.stringify({
                PairingCode: pairCode.trim().toUpperCase(),
                CodeAbonne: user.CodeAbonne,
                CodeFiliale: pairFiliale,
                Libelle: pairLibelle.trim() || undefined,
            })),
        })
            .then((res) => {
                setPairing(false)
                const v = JSON.parse(Crypter.decrypt(res.data)).Value
                if (v.StatutCode == 200 || v.StatutCode === 'OK') {
                    alert.success(v.Message || 'Borne validée — elle récupère ses credentials')
                    setOpenPairing(false)
                    setPairCode(''); setPairFiliale(''); setPairLibelle('')
                } else alert.warning(v.Message)
            })
            .catch((e) => { setPairing(false); alert.error(e.message) })
    }

    const columns: DataTableColumn[] = [
        { field: 'BorneId', headerName: '#', width: 60 },
        {
            field: 'Libelle', headerName: 'Borne', width: 220,
            renderCell: ({ value }) => (
                <span className="inline-flex items-center gap-2">
                    <MonitorSmartphone size={15} className="text-[var(--eb-primary)]" /> {value || '—'}
                </span>
            ),
        },
        { field: 'CodeFiliale', headerName: 'Filiale', width: 120 },
        {
            field: 'Statut', headerName: 'Statut', width: 120,
            renderCell: ({ value }) => (
                <Badge color={value === 'ACTIVE' ? 'green' : value === 'SUSPENDUE' ? 'amber' : 'red'}>{value}</Badge>
            ),
        },
        { field: 'DerniereActivite', headerName: 'Dernière activité', width: 180 },
        { field: 'DateActivation', headerName: 'Activée le', width: 160 },
        {
            field: 'actions', headerName: 'Actions', width: 200, sortable: false,
            renderCell: ({ row }) => (
                <div className="flex items-center gap-1.5">
                    {row.Statut === 'ACTIVE' && (
                        <Button size="sm" variant="outline" startIcon={<PauseCircle size={14} />}
                            onClick={(e) => { e.stopPropagation(); setConfirm({ option: 'SUSPEND', borne: row }) }}>
                            Suspendre
                        </Button>
                    )}
                    {row.Statut === 'SUSPENDUE' && (
                        <Button size="sm" variant="outline" startIcon={<PlayCircle size={14} />}
                            onClick={(e) => { e.stopPropagation(); setConfirm({ option: 'REACTIVATE', borne: row }) }}>
                            Réactiver
                        </Button>
                    )}
                    {row.Statut !== 'REVOQUEE' && (
                        <Button size="sm" variant="danger" startIcon={<Ban size={14} />}
                            onClick={(e) => { e.stopPropagation(); setConfirm({ option: 'REVOKE', borne: row }) }}>
                            Révoquer
                        </Button>
                    )}
                </div>
            ),
        },
    ]

    const ACTION_LABEL = { REVOKE: 'révoquer définitivement', SUSPEND: 'suspendre', REACTIVATE: 'réactiver' }

    return (
        <div>
            <PageHeader
                title="Bornes et écrans"
                subtitle="Supervision et cycle de vie des bornes self-service et des écrans LiveScreem"
                actions={tab === 'bornes' ? (
                    <div className="flex gap-2">
                        <Button variant="outline" startIcon={<RefreshCw size={16} />} onClick={LoadBornes}>Actualiser</Button>
                        <Button variant="primary" startIcon={<KeyRound size={16} />} onClick={() => setOpenPairing(true)}>
                            Valider un pairing
                        </Button>
                    </div>
                ) : undefined}
            />

            {/* Onglets Bornes / Écrans */}
            <div className="inline-flex flex-wrap items-center gap-1 p-1 rounded-xl bg-[var(--eb-bg)] border border-[var(--eb-border)] mb-4">
                {([
                    { key: 'bornes' as const, label: 'Bornes', icon: <MonitorSmartphone size={15} /> },
                    { key: 'ecrans' as const, label: 'Écrans', icon: <Tv size={15} /> },
                ]).map(t => (
                    <button
                        key={t.key}
                        type='button'
                        onClick={() => setTab(t.key)}
                        className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-colors
                            ${tab === t.key
                                ? 'bg-[var(--eb-primary)] text-white shadow-sm'
                                : 'text-[var(--eb-text-muted)] hover:text-[var(--eb-text)]'}`}
                    >
                        {t.icon}
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === 'ecrans' ? <GestionEcrans /> : (
                <Spin spinning={loading}>
                    <div className="eb-table-wrap mt-3">
                        <div className="mb-3 max-w-[260px]">
                            <Select
                                label="Filiale"
                                value={filtreFiliale}
                                onChange={(v) => setFiltreFiliale(v)}
                                options={[
                                    { value: '', label: 'Toutes les filiales' },
                                    ...filiales.map((f: any) => ({ value: f.CodeFiliale, label: `${f.CodeFiliale} — ${f.Libelle}` })),
                                ]}
                            />
                        </div>
                        <DataTable rows={rows} columns={columns} height={420} searchable />
                    </div>
                    <TableStatusBar count={rows.length} loading={loading} onRefresh={LoadBornes} />

                    {/* Modale pairing */}
                    <Modal
                        open={openPairing}
                        onClose={() => setOpenPairing(false)}
                        title="Valider le pairing d'une borne"
                        footer={
                            <Button variant="primary" onClick={validatePairing} disabled={pairing}
                                startIcon={pairing ? <RefreshCw size={16} className="animate-spin" /> : <KeyRound size={16} />}>
                                Valider la borne
                            </Button>
                        }
                    >
                        <div className="flex flex-col gap-3">
                            <Input
                                label="Code affiché sur la borne"
                                placeholder="XXXX-XXXX"
                                value={pairCode}
                                onChange={(e: any) => setPairCode(e.target.value)}
                                required
                            />
                            <Select
                                label="Filiale de rattachement"
                                value={pairFiliale}
                                onChange={setPairFiliale}
                                options={filiales.map((f: any) => ({ value: f.CodeFiliale, label: `${f.CodeFiliale} — ${f.Libelle}` }))}
                            />
                            <Input
                                label="Libellé (optionnel)"
                                placeholder="Borne entrée, borne 2…"
                                value={pairLibelle}
                                onChange={(e: any) => setPairLibelle(e.target.value)}
                            />
                            <p className="text-xs text-[var(--eb-text-muted)] flex items-center gap-1.5">
                                <Wifi size={13} /> La borne récupère ses credentials automatiquement après validation.
                            </p>
                        </div>
                    </Modal>

                    {/* Confirmation action */}
                    <Modal
                        open={!!confirm}
                        onClose={() => setConfirm(null)}
                        title="Confirmer l'action"
                        footer={
                            <div className="flex gap-2">
                                <Button variant="outline" onClick={() => setConfirm(null)}>Annuler</Button>
                                <Button variant="danger" onClick={() => confirm && manageBorne(confirm.option, confirm.borne)}>
                                    Confirmer
                                </Button>
                            </div>
                        }
                    >
                        {confirm && (
                            <p className="text-sm text-[var(--eb-text)]">
                                Voulez-vous {ACTION_LABEL[confirm.option]} la borne{' '}
                                <b>{confirm.borne.Libelle || `#${confirm.borne.BorneId}`}</b> ?
                                {confirm.option === 'REVOKE' && (
                                    <span className="block mt-2 text-[var(--eb-danger)] text-xs">
                                        Effet immédiat : la clé est effacée — la borne perd tout accès (401).
                                    </span>
                                )}
                            </p>
                        )}
                    </Modal>
                </Spin>
            )}
        </div>
    );
};

export default GestionBornes;
